<script setup lang="ts">
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  type PointerDownOutsideEvent,
} from 'reka-ui';
import { computed, onMounted, onUnmounted, useSlots, watch } from 'vue';

import CloseButton from '@/components/CloseButton.vue';
import type { VueClass } from '@/kasten-components';
import { useDialogDescriptionId } from '@/kasten-components/composables';
import { useMainStore } from '@/stores/main';
import { cnMerge } from '@/utils';

export type NewSidepanelProps = {
  show?: boolean;
  backdrop?: boolean;
  /** @deprecated use size="medium" instead */
  large?: boolean;
  left?: boolean;
  showCloseButton?: boolean;
  label?: string;
  labelId?: string;
  labelTag?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  description?: string;
  descriptionId?: string;
  headerClasses?: VueClass;
  bodyClass?: VueClass;
  size?: 'small' | 'medium' | 'large';
  handleClickOutside?: () => void;
};

defineOptions({ name: 'NewSidepanel2', inheritAttrs: false });

const emit = defineEmits(['close']);
const props = withDefaults(defineProps<NewSidepanelProps>(), {
  show: false,
  backdrop: true,
  large: false,
  left: false,
  showCloseButton: true,
  labelTag: 'h2',
  size: 'small',
});

const slots = useSlots();

const isLeft = computed(() => props.left);

const mainStore = useMainStore();

const validateProps = (label?: string, labelId?: string) => {
  if (import.meta.env.DEV && !label && !labelId && !slots.header) {
    throw new Error(
      'NewSidepanel2: label, labelId, or #header slot must be provided. Dialogs require a label for screen readers.',
    );
  }
};

const panelSize = computed(() => props.size ?? (props.large ? 'medium' : 'small'));

const handleClose = () => {
  if (!mainStore.preventSidepanelClose) {
    emit('close');
  }
};

const handleOpenChange = (open: boolean) => {
  if (!open) {
    handleClose();
  }
};

const cleanUp = () => {
  if (props.left) {
    mainStore.preventSidepanelClose = false;
  }
};

const clickOutsideHandler = (event: PointerDownOutsideEvent) => {
  event.preventDefault();
  if (props.handleClickOutside) {
    props.handleClickOutside();
  } else {
    handleClose();
  }
};

watch(
  () => props.show,
  (newVal?: boolean) => {
    if (!newVal) {
      cleanUp();
    }
    // if this Sidepanel is on the left of the screen, prevent the ESC key from closing BOTH sidepanels
    if (props.left) {
      if (newVal) {
        mainStore.preventSidepanelClose = true;
      } else {
        mainStore.preventSidepanelClose = false;
      }
    }
  },
);

onMounted(() => {
  validateProps(props.label, props.labelId);
});

const hasDescription = computed(() => !!props.description || !!slots.description);
const generatedDescriptionId = useDialogDescriptionId(hasDescription);
const resolvedDescriptionId = computed(() => props.descriptionId || generatedDescriptionId.value);

onUnmounted(cleanUp);
</script>

<template>
  <DialogRoot :open="show" @update:open="handleOpenChange">
    <DialogPortal>
      <Transition
        v-if="backdrop"
        enter-active-class="ease-in-out duration-500"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="ease-in-out duration-500"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <DialogOverlay
          v-if="show"
          class="fixed inset-0 z-[1000] w-full max-w-full bg-black/50 transition-opacity"
        />
      </Transition>

      <div
        class="fixed inset-0 z-[1000] w-full max-w-full overflow-hidden"
        :class="{ 'pointer-events-none': !show }"
      >
        <div class="relative h-full w-full overflow-hidden">
          <div
            :class="[
              'pointer-events-none absolute bottom-0 left-0 right-0 top-0 flex w-full max-w-full',
              {
                'lg:w-[75rem]': panelSize === 'large',
                'lg:w-[50rem]': panelSize === 'medium',
                'lg:w-[32rem]': panelSize === 'small',
                'sm:right-[unset]': isLeft,
                'sm:left-[unset]': !isLeft,
              },
            ]"
          >
            <Transition
              enter-active-class="transition transform duration-300 ease-out"
              leave-active-class="transition transform duration-200 ease-in"
              :enter-from-class="isLeft ? '-translate-x-full' : 'translate-x-full'"
              :leave-to-class="isLeft ? '-translate-x-full' : 'translate-x-full'"
            >
              <DialogContent
                v-if="show"
                class="k-sidepanel pointer-events-auto w-full bg-gray-50 shadow dark:border-l dark:border-l-navy-600 dark:bg-[#0d1627] dark:text-gray-400"
                :aria-describedby="resolvedDescriptionId"
                v-bind="$attrs"
                aria-modal="true"
                @pointer-down-outside="clickOutsideHandler"
              >
                <DialogDescription
                  v-if="resolvedDescriptionId"
                  :id="resolvedDescriptionId"
                  class="sr-only"
                >
                  <slot name="description">{{ description }}</slot>
                </DialogDescription>

                <div
                  data-testid="k-sidepanel-content-scroll-container"
                  class="k-sidepanel-inner flex h-full flex-col overflow-y-scroll"
                >
                  <!-- Sidepanel Header -->
                  <div
                    class="border-b border-b-black/15 bg-white px-2 py-4 dark:bg-navy-700 sm:p-6"
                  >
                    <div class="relative flex items-center justify-center">
                      <!-- as-child delegates reka's generated ID onto the slot's root element so
                           aria-labelledby resolves when consumers provide their own heading. -->
                      <DialogTitle
                        :as-child="!!$slots.header"
                        :as="props.labelTag"
                        :class="
                          cnMerge([
                            'm-0 truncate p-0 text-xl font-bold leading-[1.2] sm:text-2xl',
                            props.headerClasses,
                          ])
                        "
                      >
                        <slot name="header">
                          {{ label }}
                        </slot>
                      </DialogTitle>

                      <CloseButton
                        data-testid="k-sidepanel-close-button"
                        @click="emit('close')"
                        aria-label="Close"
                        :class="[
                          'absolute right-0 top-1/2 -translate-y-1/2',
                          { 'sr-only': !showCloseButton },
                        ]"
                      />
                    </div>
                  </div>

                  <!-- Sidepanel Body -->
                  <div
                    class="k-sidepanel-body flex-1 overflow-y-auto [@media(max-height:400px)]:flex-none [@media(max-height:400px)]:overflow-y-visible"
                  >
                    <div
                      :class="
                        cnMerge([
                          'k-sidepanel-body-content',
                          {
                            'px-2 py-4 sm:p-6': panelSize === 'small',
                            'px-6 pb-20 pt-8 sm:px-12':
                              panelSize === 'medium' || panelSize === 'large',
                          },
                          props.bodyClass,
                        ])
                      "
                    >
                      <slot name="body">Sidepanel Body Content</slot>
                    </div>
                  </div>

                  <!-- Sticky Footer -->
                  <div
                    v-if="$slots.footer"
                    class="flex flex-wrap justify-center gap-4 border-t border-gray-200 bg-white px-2 py-6 dark:border-navy-700 dark:bg-navy-900 sm:px-6"
                  >
                    <slot name="footer"></slot>
                  </div>
                </div>
              </DialogContent>
            </Transition>
          </div>
        </div>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="scss" scoped>
.k-sidepanel {
  :deep() {
    img {
      width: 100%;
      margin: 1rem 0;
    }

    section {
      padding: 0.5rem 1rem;
      margin-bottom: 1rem;
    }

    .form-footer {
      position: sticky;
      bottom: 0;
      z-index: 20;
      place-content: center;
      background: white;
      width: 100%;
      padding: 1rem;
      border-top: 1px solid rgba(0, 0, 0, 0.15);
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;

      @media (min-width: 640px) {
        padding: 1.5rem;
      }

      // Reflow fix (WCAG 1.4.10 / K10-29636): at high zoom the viewport is very
      // short and a pinned footer overlaps the entire body, so let it scroll away.
      @media (max-height: 400px) {
        position: static;
      }
    }
  }
}
</style>
